import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Warehouse, AlertTriangle, TrendingUp, DollarSign, Search, Loader2, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { isRestricted, MASK } from "../../utils/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// ─── Inline editable price cell ───
// Click → input. Enter / blur → save (PATCH /api/admin/products/{id}).
// Esc → cancel. Optimistic update on the local row + summary auto-refresh.
const PriceCell = ({ productId, field, value, color, allowEmpty = false, onSaved, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const [busy, setBusy] = useState(false);

  const beginEdit = () => {
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  };

  const commit = async () => {
    if (busy) return;
    const trimmed = draft.trim();
    if (!allowEmpty && trimmed === "") {
      setEditing(false);
      return;
    }
    const num = trimmed === "" ? null : Number(trimmed);
    if (trimmed !== "" && (isNaN(num) || num < 0)) {
      toast.error("أدخل رقماً صحيحاً");
      return;
    }
    // No change — exit silently
    if ((num ?? null) === (value ?? null)) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/products/${productId}`, { [field]: num });
      toast.success("تم التحديث");
      setEditing(false);
      onSaved && onSaved(productId, field, num);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" dir="ltr">
        <input
          autoFocus
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          data-testid={`${testid}-input`}
          className="w-24 px-2 py-1 rounded border-2 border-[#3B82F6] text-sm font-bold bg-white text-[#111814]"
        />
        {busy && <Loader2 className="w-3 h-3 animate-spin text-[#3B82F6]" />}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={beginEdit}
      data-testid={testid}
      title="انقر لتعديل · Enter لتثبيت · Esc للإلغاء"
      className="font-bold hover:bg-[#EFF6FF] hover:ring-2 hover:ring-[#3B82F6]/30 rounded px-1.5 py-0.5 cursor-text transition-colors"
      style={{ color }}
    >
      {value != null && value > 0 ? _fmt(value) : <span className="text-[#5C6E64] text-xs">—</span>}
    </button>
  );
};

// ─── Inline editable stock cell ───
const StockCell = ({ productId, value, onSaved, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));
  const [busy, setBusy] = useState(false);

  const commit = async () => {
    if (busy) return;
    const trimmed = draft.trim();
    if (trimmed === "") { setEditing(false); return; }
    const num = parseInt(trimmed, 10);
    if (isNaN(num) || num < 0) { toast.error("أدخل رقماً صحيحاً"); return; }
    if (num === value) { setEditing(false); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/products/${productId}`, { stock: num });
      toast.success("تم التحديث");
      setEditing(false);
      onSaved && onSaved(productId, "stock", num);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    } finally {
      setBusy(false);
    }
  };

  const isOut = value === 0;
  const isLow = value > 0 && value < 10;
  const tone = isOut ? "text-[#E85D45]" : isLow ? "text-[#F5A623]" : "text-[#7ED321]";

  if (editing) {
    return (
      <div className="flex items-center gap-1" dir="ltr">
        <input
          autoFocus
          type="number"
          step="1"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          data-testid={`${testid}-input`}
          className="w-20 px-2 py-1 rounded border-2 border-[#3B82F6] text-sm font-bold bg-white text-[#111814]"
        />
        {busy && <Loader2 className="w-3 h-3 animate-spin text-[#3B82F6]" />}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => { setDraft(String(value)); setEditing(true); }}
      data-testid={testid}
      title="انقر لتعديل المخزون"
      className={`font-bold hover:bg-[#EFF6FF] hover:ring-2 hover:ring-[#3B82F6]/30 rounded px-1.5 py-0.5 cursor-text transition-colors ${tone}`}
    >
      {isOut ? "نافذ" : value}
    </button>
  );
};

const InventoryReport = ({ refreshTick = 0 }) => {
  const { user } = useAuth();
  const fmt = (n) => (isRestricted(user) ? MASK : _fmt(n));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      // Cache-buster (_t=<ts>) defeats any browser / edge / CDN cache so
      // a freshly-added product ALWAYS shows up on the next refresh.
      const { data } = await axios.get(`${API}/admin/inventory`, {
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      setData(data);
    } catch {
      toast.error("فشل تحميل كشف المخزن");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Refetch on mount AND whenever the parent bumps refreshTick — i.e.
  // whenever ANY product is created/edited/deleted anywhere in the app
  // (Products page, Brands page modal, etc.). Newly added products
  // appear in the inventory report the moment they're saved.
  useEffect(() => {
    fetchData();
  }, [refreshTick]);

  const handleManualRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Optimistic local-row patch + auto refresh aggregates so the summary
  // cards update without forcing the user to wait or scroll.
  const handleSaved = (productId, field, newValue) => {
    setData((d) => {
      if (!d) return d;
      const products = d.products.map((p) => {
        if (p.id !== productId) return p;
        const np = { ...p, [field]: newValue };
        // Recompute per-row derived figures so the table mirrors backend reality.
        const stock = field === "stock" ? newValue : (p.stock || 0);
        const cost = field === "cost_price" ? (newValue || 0) : (p.cost_price || 0);
        const sell = field === "price" ? (newValue || 0) : (p.selling_price || 0);
        np.cost_price = cost;
        np.selling_price = sell;
        np.stock = stock;
        np.stock_cost_value = stock * cost;
        np.stock_sale_value = stock * sell;
        np.expected_profit = (sell - cost) * stock;
        // Immediately flip the green-dot marker on stock edits so the admin
        // gets visual feedback without waiting for the /admin/inventory refetch.
        if (field === "stock") {
          np.stock_manually_edited_at = new Date().toISOString();
        }
        return np;
      });
      return { ...d, products };
    });
    // Refresh server-side aggregates after a short debounce
    setTimeout(fetchData, 250);
  };

  if (loading) {
    return <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>;
  }
  if (!data) return null;

  // Guard against products with missing name_ar / name (throwing on
  // .toLowerCase would silently break the whole filter and hide the
  // just-added product). Any missing field is treated as an empty
  // string so we compare only what we have.
  const q = (search || "").toLowerCase();
  const filtered = data.products.filter((p) => {
    const ar = (p.name_ar || "").toLowerCase();
    const en = (p.name || "").toLowerCase();
    return ar.includes(q) || en.includes(q);
  });

  return (
    <div data-testid="inventory-report" className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="inv-total-cost"
          className="bg-white border border-[#E4EBE6] rounded-2xl p-4"
        >
          <Warehouse className="w-5 h-5 text-[#4A90E2] mb-2" />
          <p className="text-xs text-[#5C6E64]">قيمة المخزن (شراء)</p>
          <p className="font-bold text-xl text-[#111814]">{fmt(data.summary.total_cost_value)}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          data-testid="inv-total-sale"
          className="bg-white border border-[#E4EBE6] rounded-2xl p-4"
        >
          <DollarSign className="w-5 h-5 text-[#7ED321] mb-2" />
          <p className="text-xs text-[#5C6E64]">قيمة المخزن (بيع)</p>
          <p className="font-bold text-xl text-[#111814]">{_fmt(data.summary.total_sale_value)}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="inv-expected-profit"
          className="bg-gradient-to-br from-[#7ED321] to-[#5fa617] text-white rounded-2xl p-4"
        >
          <TrendingUp className="w-5 h-5 mb-2 opacity-90" />
          <p className="text-xs opacity-90">الربح المتوقع</p>
          <p className="font-bold text-xl">{fmt(data.summary.expected_profit)}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          data-testid="inv-out-stock"
          className="bg-white border border-[#E4EBE6] rounded-2xl p-4"
        >
          <AlertTriangle className="w-5 h-5 text-[#E85D45] mb-2" />
          <p className="text-xs text-[#5C6E64]">نافذ / منخفض</p>
          <p className="font-bold text-xl text-[#111814]">
            {data.summary.out_of_stock_count} <span className="text-sm text-[#F5A623]">/ {data.summary.low_stock_count}</span>
          </p>
        </motion.div>
      </div>

      {/* Hint banner explaining inline edit */}
      <div className="bg-[#EFF6FF] border border-[#3B82F6]/30 rounded-xl px-3 py-2 text-xs text-[#1E3A5F] flex items-center gap-2">
        <Check className="w-4 h-4 text-[#3B82F6]" />
        <span>
          <b>تلميح:</b> انقر على «الشراء» أو «البيع» أو «سعر B+» أو «المخزون» مباشرة لتعديلها. اضغط <b>Enter</b> للحفظ أو <b>Esc</b> للإلغاء.
        </span>
      </div>

      {/* Search + manual refresh */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute right-3 top-3 text-[#5C6E64]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث في المنتجات..."
            data-testid="inv-search"
            className="w-full pl-4 pr-10 py-2.5 rounded-full border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white text-sm"
          />
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={refreshing}
          className="flex-shrink-0 flex items-center gap-1 px-3 py-2.5 rounded-full bg-[#2D4A3E] hover:bg-[#243d33] text-white text-xs font-bold transition disabled:opacity-60"
          data-testid="inv-refresh"
          title="جلب أحدث قائمة منتجات من الخادم"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          <span className="hidden md:inline">تحديث</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E4EBE6] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="inv-table">
          <thead className="bg-[#F3F6F4] text-[#2D4A3E] text-xs font-bold">
            <tr>
              <th className="text-right px-3 py-3">المنتج</th>
              <th className="text-right px-3 py-3">القسم</th>
              <th className="text-right px-3 py-3">الشراء</th>
              <th className="text-right px-3 py-3">البيع</th>
              <th className="text-right px-3 py-3 text-[#7C3AED]">سعر B+</th>
              <th className="text-right px-3 py-3">المخزون</th>
              <th className="text-right px-3 py-3 hidden md:table-cell">قيمة الشراء</th>
              <th className="text-right px-3 py-3">الربح المتوقع</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              // Red/Green dot next to product name: green if stock was manually
              // edited in the last 30 days, red otherwise. The month-long window
              // lets the admin see at a glance which items were touched during
              // this month's physical inventory count.
              const editedAt = p.stock_manually_edited_at;
              let recentEdit = false;
              if (editedAt) {
                const ts = new Date(editedAt).getTime();
                recentEdit = !isNaN(ts) && (Date.now() - ts) <= 30 * 24 * 60 * 60 * 1000;
              }
              return (
              <tr key={p.id} data-testid={`inv-row-${p.id}`} className="border-t border-[#E4EBE6] hover:bg-[#FAFCFA]">
                <td className="px-3 py-3 font-medium text-[#111814]">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      data-testid={`inv-edit-dot-${p.id}`}
                      title={recentEdit ? "تم تعديل المخزون خلال آخر شهر" : "لم يتم تعديل المخزون يدوياً"}
                      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                        recentEdit ? "bg-[#7ED321]" : "bg-[#E85D45]"
                      }`}
                    />
                    <span>{p.name_ar}</span>
                  </span>
                </td>
                <td className="px-3 py-3 text-[#5C6E64]">{p.category}</td>
                <td className="px-3 py-3">
                  <PriceCell
                    productId={p.id}
                    field="cost_price"
                    value={p.cost_price}
                    color="#4A90E2"
                    onSaved={handleSaved}
                    testid={`inv-cost-${p.id}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <PriceCell
                    productId={p.id}
                    field="price"
                    value={p.selling_price}
                    color="#2D4A3E"
                    onSaved={handleSaved}
                    testid={`inv-sell-${p.id}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <PriceCell
                    productId={p.id}
                    field="wholesale_price"
                    value={p.wholesale_price}
                    color="#7C3AED"
                    allowEmpty
                    onSaved={handleSaved}
                    testid={`inv-bplus-${p.id}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <StockCell
                    productId={p.id}
                    value={p.stock || 0}
                    onSaved={handleSaved}
                    testid={`inv-stock-${p.id}`}
                  />
                </td>
                <td className="px-3 py-3 hidden md:table-cell text-[#5C6E64]">{_fmt(p.stock_cost_value)}</td>
                <td className="px-3 py-3 text-[#7ED321] font-bold">{fmt(p.expected_profit)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center py-8 text-[#5C6E64]">لا توجد منتجات</p>
        )}
      </div>
    </div>
  );
};

export default InventoryReport;
